import * as qs from 'qs';
import { pick, pickBy, identity, omit } from 'lodash';
import { FormatLinkFields, Format, Platform, AppOption, TrackerOption, LinkFields, AdEnvironment } from '../types';
import { NOT_ENCODABLE_KEYS, ULINK_PARAM_PREFIX } from './formatLink';

interface CommonFields extends Partial<FormatLinkFields> {
  format: Format;
  queryString?: string;
}

export const RAW_ULINK_REGEX = /^([^\.\/]+)\.(([^\.]{2})\.)?(adj\.st|go\.link)$/;

const DOMAIN_REGEX = /^https:\/\/app\.(([^.]{2})\.)?adjust\.com/;

const JSR_REGEX = new RegExp(`${DOMAIN_REGEX.source}/jsr`);
const TDL_REGEX = new RegExp(`${DOMAIN_REGEX.source}/([^?]+)(\\?|$)`);
const ULINK_REGEX = /^https:\/\/([^.]+\.(([^.]{2})\.)?(adj\.st|go\.link))\/?([^?]*)/;
const APP_SCHEME_REGEX = /^(\S+)(:\/\/)/;
const RAW_KEYS = [
  ...NOT_ENCODABLE_KEYS,
  'deep_link',
  'deeplink_js',
  'fallback',
  'redirect',
  'redirect_macos',
  'redirect_ios',
  'redirect_android',
] as const;
const RAW_KEYS_REGEX = new RegExp(`(^|&)(${RAW_KEYS.join('|')})=`, 'g');

interface QueryParams {
  [key: string]: string | undefined;
}

type RawQueryParams = {
  [key in typeof RAW_KEYS[number]]: string | undefined;
};

/**
 * Parse format, token and other parameters that can be parsed
 * without platform/app/token details
 */
export const parseCommonFields = (link: string): CommonFields => {
  const queryString = link.replace(/^[^?]+(\?|$)/, '');
  const queryParams = qs.parse(queryString) as QueryParams;

  // in JSR format `url` query parameter should contain ulink
  // in such case call method again with ulink as a parametr to get details
  const jsr = link.match(JSR_REGEX);
  if (jsr && queryParams.url) {
    const ulinkResult = parseCommonFields(queryParams.url);
    return {
      ...ulinkResult,
      dataResidency: jsr[2],
      format: Format.jsr,
    };
  }

  const tdl = TDL_REGEX.exec(link);
  if (tdl) {
    return {
      queryString,
      format: Format.tdl,
      dataResidency: tdl[2],
      trackerToken: tdl[3],
    };
  }

  const ulink = ULINK_REGEX.exec(link);
  if (ulink && queryParams.adj_t) {
    return {
      queryString,
      format: Format.ulink,
      dataResidency: ulink[3],
      rawUniversalLink: ulink[1],
      iosUniversalLinkPath: ulink[5],
      trackerToken: queryParams.adj_t,
    };
  }

  throw new Error('Link is not recognized');
};

/**
 * Parse non encodable query parameters (those parameters can have special characters like `&`).
 */
const parseRaw = (queryString: string) => {
  const result: { [key: string]: string } = {};
  let match: RegExpExecArray | null = null;
  let prev: { key: string; match: RegExpExecArray } | null = null;

  while ((match = RAW_KEYS_REGEX.exec(queryString)) !== null) {
    if (prev) {
      result[prev.key] = queryString.slice(prev.match.index + prev.match[0].length, match.index);
    }
    prev = { key: match[2], match };
  }

  if (prev) {
    result[prev.key] = queryString.slice(prev.match.index + prev.match[0].length);
  }

  return result;
};

/**
 * Returns `true` if selected tracker doesn't exist in app trackers selector (tracker isn't in first level)
 */
const isManualTracker = ({ app, tracker }: { app: AppOption; tracker: TrackerOption }) =>
  !app.trackerOptions.find(to => to.value === tracker.value);

/**
 * Returns query string that entered by user in path field
 */
const getPathQueryString = (queryParams: QueryParams) => {
  const actualParams = pickBy(omit(queryParams, [...RAW_KEYS, 't']), identity);
  return qs.stringify(actualParams);
};

/**
 * Parse rest parameters depending on platform/app/token details
 */
export const parseFields = (
  { queryString = '', ...commonFields }: CommonFields,
  trackersApps: { tracker: TrackerOption; app: AppOption }[],
) => {
  const [appTracker, androidAppTracker] = trackersApps;
  const { app, tracker } = appTracker;
  let fields: LinkFields = {
    ...commonFields,
    platform: app.platform,
    targetApp: app.value,
    targetTracker: tracker.value,
    targetTrackerManual: isManualTracker(appTracker),
  };

  if (androidAppTracker) {
    // only multiplatform link with ios (first) and android (second) application can have 2 trackers
    fields.platform = Platform.multi;
    fields.androidApp = androidAppTracker.app.value;
    fields.androidTracker = androidAppTracker.tracker.value;
    fields.androidTrackerManual = isManualTracker(androidAppTracker);
  }

  // prepend queryString with `&` charater for easier chunks search
  queryString = `&${queryString}`;

  // query keys in ulink/jsr formats have special prefix
  // remove it to use same variable names for all formats
  if ([Format.ulink, Format.jsr].includes(fields.format)) {
    queryString = queryString.replace(new RegExp(`&${ULINK_PARAM_PREFIX}`, 'g'), '&');
  }

  const { additionalParameters } = tracker;
  if (additionalParameters) {
    Object.keys(additionalParameters).forEach(key => {
      if (!additionalParameters[key]) return;

      // if parsed link doesn't have at least one additional parameter
      // this link is not valid anymore
      const trackingParam = `&${key}=${additionalParameters[key]}`;
      if (!queryString.includes(trackingParam)) {
        throw new Error(`Link does not have one of the tracking parameters: ${trackingParam}`);
      }
      queryString = queryString.replace(trackingParam, '');
    });
  }

  const rawQueryParams = parseRaw(queryString) as RawQueryParams;
  const queryParams = qs.parse(queryString) as QueryParams;

  // add non encodable parameters
  const { gps_adid: gpsAdid, ...notEncodableFields } = pick(rawQueryParams, NOT_ENCODABLE_KEYS);
  fields = {
    ...fields,
    ...notEncodableFields,
    gpsAdid,
  };

  // add encodable parameters
  fields.fallbackUrl = queryParams.fallback;
  fields.redirectUrl = queryParams.redirect;
  fields.redirectMacos = queryParams.redirect_macos;
  fields.redirectIos = queryParams.redirect_ios;
  fields.redirectAndroid = queryParams.redirect_android;

  if ([Platform.ios, Platform.multi].includes(fields.platform) && !fields.adEnvironment) {
    // old ios/multi links can be without `adEnvironment` field
    // is such case use `other` as default because it has all possible formats
    // except cases when link has `deeplink_js` param
    fields.adEnvironment = rawQueryParams.deeplink_js ? AdEnvironment.organicTwitter : AdEnvironment.other;
  }

  // extract app scheme and path from deep_link parameter
  // and put it in correct fields depending on platform
  let appScheme = '';
  let path = '';
  const deeplink = queryParams.deep_link;
  if (deeplink) {
    const result = APP_SCHEME_REGEX.exec(deeplink);
    if (result) {
      appScheme = result[0];
      path = deeplink.replace(APP_SCHEME_REGEX, '');
      if (fields.format === Format.tdl) {
        switch (fields.platform) {
          case Platform.android:
            fields.androidAppScheme = appScheme;
            fields.androidDeeplinkPath = path;
            break;

          case Platform.ios:
            fields.iosAppScheme = appScheme;
            fields.iosUniversalLinkPath = path;
            break;

          case Platform.multi:
            fields.commonAppScheme = appScheme;
            fields.commonDeeplinkPath = path;
            break;
        }
      } else if (fields.platform === Platform.multi) {
        fields.androidAppScheme = appScheme;
        fields.androidDeeplinkPath = path;
      }
    }
  }

  // unknown query parameters are specified by the user
  // and should be added to the path
  const pathQueryString = getPathQueryString(queryParams);
  if (pathQueryString && [Format.ulink, Format.jsr].includes(fields.format)) {
    if (!fields.iosUniversalLinkPath) fields.iosUniversalLinkPath = '';
    fields.iosUniversalLinkPath += `?${pathQueryString}`;
  }

  const androidTrackerApp = trackersApps.find(ta => [Platform.android, Platform.multi].includes(ta.app.platform));
  if (androidTrackerApp?.app.androidAppScheme) {
    // if android application is selected and it has `andriodAppScheme` field
    // than the field should be prefilled
    if (fields.androidAppScheme && fields.androidAppScheme !== androidTrackerApp.app.androidAppScheme) {
      throw new Error('Link contains invalid android app scheme parameter');
    }
    fields.androidAppScheme = androidTrackerApp.app.androidAppScheme;

    // prefill same as ios path checkbox if necessary
    if (deeplink) fields.androidDeeplinkPath = path;
  }

  if (fields.iosUniversalLinkPath && (!deeplink || fields.androidDeeplinkPath === fields.iosUniversalLinkPath)) {
    fields.androidDeeplinkPath = fields.iosUniversalLinkPath;
    fields.androidDeeplinkPathSameAsIos = true;
  }

  // exclude keys with nullable values
  return pickBy(fields, identity) as LinkFields;
};

/**
 * Returns parts of raw universal link if link is correct
 */
export const parseRawUlink = (rawUlink: string) => {
  const result = RAW_ULINK_REGEX.exec(rawUlink);
  if (!result) {
    return undefined;
  }

  return {
    prefix: result[1],
    residency: result[3],
    postfix: result[4],
  };
};
